import { useMemo, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';

import { Popover, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import update from 'immutability-helper';

import useGetPushCategories from '@hooks/queries/push/useGetPushCategories';
import useGetPushes from '@hooks/queries/push/useGetPushes';

import type { GetPushesQuery } from '@api/push/getPushes';

import Sort from '@components/Sort';
import TableHeader from '@components/TableHeader';

import PushDetail from './PushDetail';
import * as S from './styled';
import type * as T from './type';

const PAGE_SIZE: number = 10;

function SituationTable() {
  const [sendContentType, setMessageType] = useState<T.SendContentType>('push');
  const [selectedSituations, setSelectedSituations] = useState<T.PushSituationTableDataSource>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [page, setPage] = useState<number>(1);
  const [dir, setDir] = useState<GetPushesQuery['dir']>('desc');

  const {
    data: pushes,
    total,
    isLoading,
  } = useGetPushes({ page, q: searchValue, dir, limit: PAGE_SIZE });
  const { data: pushCategories } = useGetPushCategories();

  const dataSource: T.PushSituationTableDataSource = useMemo(() => {
    return pushes.map(v => ({ ...v, key: v.notification.id }));
  }, [pushes]);

  const columns: ColumnsType<T.PushSituationTableData> = [
    {
      key: 'id',
      dataIndex: 'notification',
      title: '번호',
      width: '10%',
      render: ({ id }: { id: number }) => id,
    },
    {
      key: 'categoryId',
      dataIndex: 'notification',
      title: '유형',
      width: '10%',
      render: ({ categoryId }) => {
        console.log('categoryId', pushCategories, categoryId);
        const findCategoryName = pushCategories
          ? pushCategories?.find(({ id }) => id === categoryId)?.name || ''
          : '';

        return <>{findCategoryName}</>;
      },
    },
    {
      key: 'message',
      dataIndex: 'notification',
      title: '메시지',
      ellipsis: true,
      width: '30%',
      render: ({ message, title }) => <S.Message>{message ?? title}</S.Message>,
    },
    {
      key: 'date',
      dataIndex: 'date',
      title: () => {
        const toggleSort = () => setDir(dir === 'asc' ? 'desc' : 'asc');

        return <Sort title='발송일자' dir={dir} onClick={toggleSort} />;
      },
      width: '20%',
      render(_, { notification: { sendAt, createdAt } }) {
        const isSendAt = sendAt && dayjs(sendAt).isAfter(dayjs(new Date()));

        return (
          <>
            {isSendAt
              ? dayjs(sendAt).format('YYYY-MM-DD HH:mm')
              : dayjs(createdAt).format('YYYY-MM-DD HH:mm')}
          </>
        );
      },
    },
    {
      key: 'total',
      dataIndex: 'total',
      title: '발송결과',
      width: '15%',
      render: (_, push) => {
        const {
          notification: { id, sendAt },
          successCount,
          errorCount,
          pendingCount,
          rejectCount,
        } = push;
        const total = successCount + errorCount + pendingCount + rejectCount;
        const isBefore = dayjs().isBefore(dayjs(sendAt));

        if (isBefore) return <>전송 대기중</>;

        return (
          <S.SendResultText>
             {total}
          </S.SendResultText>
        );
      },
    }
  ];

  const select = (data: T.PushSituationTableData, selected: boolean) => {
    setSelectedSituations(prevState => {
      if (selected) return [...prevState, data];

      const findIndex = prevState.findIndex(v => v.notification.id === data.notification.id);
      if (typeof findIndex === 'undefined') return prevState;

      return update(prevState, {
        $splice: [[findIndex, 1]],
      });
    });
  };
  const selectAll = (selected: boolean, data: T.PushSituationTableDataSource) => {
    setSelectedSituations(prevState => (selected ? [...prevState, ...data.filter(v => v)] : []));
  };

  const handleSendContentType = ({ currentTarget: { name } }: MouseEvent<HTMLButtonElement>) =>
    setMessageType(name as T.SendContentType);

  const searchMessage = (value: string) => {
    if (value.length < 2) {
      message.info('검색어는 2글자 이상이어야 합니다.');
      return false;
    }

    setPage(1);
    setSearchValue(value.length > 1 ? value : undefined);
  };

  const handleSearchText = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (!value && searchValue) {
      setPage(1);
      setSearchValue(undefined);
    }
  };

  return (
    <>
      <TableHeader
        buttons={[
          { text: '푸시알림', buttonProps: { name: 'push', onClick: handleSendContentType } },
          // { text: '사서함', buttonProps: { name: 'pobox', onClick: handleSendContentType } },
          // { text: '메일', buttonProps: { name: 'mail', onClick: handleSendContentType } },
        ]}
        searchInput={true}
        inputProps={{
          placeholder: '메시지 검색',
          onChange: handleSearchText,
          onSearch: searchMessage,
        }}
      />
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedSituations.map(v => v.key),
          onSelect: select,
          onSelectAll: selectAll,
        }}
        pagination={{
          total,
          current: page,
          pageSize: PAGE_SIZE,
          position: ['bottomCenter'],
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </>
  );
}

export default SituationTable;
